import {Role} from "@/src/modules/users/types";

import {meApi} from "../api/me";
import type {BoardSeat} from "../types/board-seat";
import {
  flattenGroupedPermissions,
  hasGroupedPermission,
  normalizeGroupedPermissions,
} from "../lib/permissions";
import {getMeHouseholdId} from "../lib/get-me-household-id";
import {parseAppLocale, parseLocaleOverride, setAppLocale} from "../i18n";
import {resolveLocaleEnabled} from "../lib/locale-enabled";
import {queryClient} from "../lib/query-client";
import CoreService from "../services/core";
import {useMeStore} from "../store/me";
import type {Me} from "../types";
import {residentContextService} from "./resident-context";
import {userPropertyService} from "./user-property";

class MeService extends CoreService {
  async getMe(): Promise<Me> {
    const response = await meApi.get();
    return this.applyMePayload(
      (response.data as unknown as {data: Me & {roles?: Role[]; role?: Role}}).data,
    );
  }

  async updateLocale(locale: "en" | "es" | null): Promise<Me> {
    const response = await meApi.patchLocale(locale);
    const payload = (response.data as unknown as {data?: Me & {roles?: Role[]; role?: Role}}).data;
    const hasRole = Boolean(payload?.role ?? payload?.roles?.[0]);
    const me = hasRole && payload ? await this.applyMePayload(payload) : await this.getMe();
    queryClient.setQueryData(["me"], me);
    return me;
  }

  private async applyMePayload(
    payload: Me & {roles?: Role[]; role?: Role},
  ): Promise<Me> {
    const roles = payload.roles ?? (payload.role ? [payload.role] : []);
    const role = roles[0] ?? payload.role;
    if (!role) {
      throw new Error("User profile is missing a role");
    }
    const {roles: _roles, role: _role, ...rest} = payload as Me & {
      roles?: Role[];
      role?: Role;
      house_hold?: {id?: number; name?: string} | null;
    };

    const normalizeRoleName = (name: string) => name.trim().toLowerCase();

    const boardSeats: BoardSeat[] = Array.isArray(
      (payload as {board_seats?: unknown}).board_seats,
    )
      ? (payload as {board_seats: BoardSeat[]}).board_seats
      : [];

    const me = {
      ...rest,
      locale: parseAppLocale(payload.locale),
      locale_override: parseLocaleOverride(payload.locale_override),
      role,
      board_seats: boardSeats,
      house_hold_id: getMeHouseholdId(rest),
      effective_permissions: payload.effective_permissions
        ? normalizeGroupedPermissions(payload.effective_permissions)
        : undefined,
      direct_permissions: payload.direct_permissions
        ? normalizeGroupedPermissions(payload.direct_permissions)
        : undefined,
      impersonation: payload.impersonation ?? null,
      isAdmin: roles.some((r) => normalizeRoleName(r.name) === "admin"),
      isSuperAdmin: roles.some((r) => normalizeRoleName(r.name) === "super_admin"),
      isSupervisor: normalizeRoleName(role.name) === "supervisor",
      isSeniorSupervisor: normalizeRoleName(role.name) === "senior supervisor",
      isPostCommander: normalizeRoleName(role.name) === "post commander",
      isPropertyManager: normalizeRoleName(role.name) === "property manager",
      isDispatch: normalizeRoleName(role.name) === "dispatch",
      isDispatchSupervisor: normalizeRoleName(role.name) === "dispatch supervisor",
      isOfficer: normalizeRoleName(role.name) === "officer",
      isResident: normalizeRoleName(role.name) === "resident",
      isPatrolOfficer: normalizeRoleName(role.name) === "patrol officer",
    };

    useMeStore.setState({
      me,
    });

    if (me.isResident) {
      residentContextService.initFromMe(me);
      const selectedHouseholdId = residentContextService.getSelectedHouseholdId(me);
      if (selectedHouseholdId != null && selectedHouseholdId !== me.house_hold_id) {
        useMeStore.setState({
          me: {...me, house_hold_id: selectedHouseholdId},
        });
      }
    } else {
      residentContextService.clearContext();
    }

    userPropertyService.initUserProperty(useMeStore.getState().me!);

    const companyPayload = payload.company as
      | {locale?: unknown; locale_enabled?: unknown}
      | null
      | undefined;
    if (companyPayload) {
      const {useCompanyStore} = await import("@/src/modules/company/store");
      const existing = useCompanyStore.getState().company;
      if (existing) {
        const localePatch =
          companyPayload.locale === "en" || companyPayload.locale === "es"
            ? {locale: companyPayload.locale as "en" | "es"}
            : {};
        useCompanyStore.setState({
          company: {
            ...existing,
            ...localePatch,
            locale_enabled: resolveLocaleEnabled(companyPayload.locale_enabled),
          },
        });
      }
    }

    await setAppLocale(me.locale);

    return useMeStore.getState().me!;
  }

  clearMe(): void {
    useMeStore.setState({
      me: null,
    });
    residentContextService.clearContext();
  }

  getHasPermission(permission: string): boolean {
    const me = useMeStore.getState().me;
    if (!me) return false;
    if (me.effective_permissions) {
      return hasGroupedPermission(me.effective_permissions, permission);
    }
    return me.role.permissions.some((p) => p.name === permission);
  }

  getEffectivePermissionNames(): Set<string> {
    const me = useMeStore.getState().me;
    if (!me) return new Set();
    if (me.effective_permissions) {
      return flattenGroupedPermissions(me.effective_permissions);
    }
    return new Set(me.role.permissions.map((p) => p.name));
  }
}

export const meService = new MeService();
